import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    signal,
    ViewEncapsulation,
    ViewChild,
    QueryList,
    ViewChildren,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { CustomAttributes } from "../../../pojo/CustomAttributes";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { Bind } from "../../../decorators/bind.decorator";
import CustAttrObj = CustomAttributes.CustAttrObj;
import { Item } from "../../../pojo/Item";
import { S25Util } from "../../../util/s25-util";
import { Proto } from "../../../pojo/Proto";
import ServiceStatus = Proto.ServiceStatus;
import { ContactService } from "../../../services/contact.service";
import { UserprefService } from "../../../services/userpref.service";
import { OrganizationService } from "../../../services/organization.service";
import CustAttrCode = CustomAttributes.CustAttrCode;
import { SpaceService } from "../../../services/space.service";
import { AttachmentService } from "../../../services/attachment.service";
import { S25File } from "../../s25-file-upload/s25.file.upload.component";
import { S25EditableCustomAttributeComponent } from "../../s25-editable/s25.editable-custom-attribute/s25.editable-custom-attribute.component";

@TypeManagerDecorator("s25-ng-object-details-custom-attr")
@Component({
    selector: "s25-ng-object-details-custom-attr",
    template: `
        @if (init()) {
            @for (attr of customAttrs; track attr.itemId) {
                @let hasFadePreview = attr.itemTypeId === AttrType.LongText && attr.itemName?.length > 350;
                @let isObjectType = typeof attr.itemTypeId === "number";
                @let needReadOnlyEl =
                    isObjectType || attr.itemTypeId === AttrType.Time || attr.itemTypeId === AttrType.DateTime;
                @let attrValueFormat =
                    isObjectType
                        ? { itemId: attr.itemId, itemName: attr.itemName, itemTypeId: attr.itemTypeId }
                        : attr.itemTypeId === AttrType.Image
                          ? attr.imageId
                          : attr.itemName;
                <!-- mobile app needs z-index values for object dropdowns, datepickers, etc for overlay issues -->
                @let increasedZIndex = isObjectType || dateTimeTypes.includes(attr.itemTypeId);

                <div
                    class="attr-container c-margin-bottom--half"
                    [ngClass]="{
                        'fade-preview-container': hasFadePreview && fadePreview(),
                        'long-text': attr.itemTypeId === AttrType.LongText,
                        'read-only': !canEdit && needReadOnlyEl,
                        'increase-zindex': increasedZIndex,
                    }"
                    [style.--index-val]="(increasedZIndex && customAttrs.length - $index) || 0"
                    (click)="applyFadePreview(hasFadePreview)"
                >
                    @if (canEdit) {
                        <button class="c-textButton" (click)="save(attr, 'del', attrValueFormat)">
                            <s25-ng-icon [type]="'close'"></s25-ng-icon>
                        </button>
                    }
                    @if (needReadOnlyEl && !canEdit) {
                        <label>{{ attr.itemLabel }}:</label>
                        @if (isObjectType) {
                            <s25-item-generic
                                [includeTypeIcon]="true"
                                [modelBean]="{
                                    itemTypeId: +attr.itemTypeId,
                                    itemId: attr.itemId,
                                    itemName: attr.itemName,
                                }"
                                [hideLabel]="true"
                            ></s25-item-generic>
                        } @else if (attr.itemTypeId === AttrType.Time) {
                            <div>{{ S25Util.date.parseTime(attr.itemName) | timeFormat: timePref }}</div>
                        } @else if (attr.itemTypeId === AttrType.DateTime) {
                            <div>{{ S25Util.date.parse(attr.itemName) | dateFormat: dateTimePref }}</div>
                        }
                    }
                    @if (canEdit || (!needReadOnlyEl && !canEdit)) {
                        <s25-ng-editable-custom-attribute
                            [attributeId]="attr.custAttrId"
                            [attributeType]="attr.itemTypeId"
                            [attributeName]="attr.itemLabel"
                            [value]="attrValueFormat"
                            [multiVal]="attr.multi_val ? 1 : 0"
                            [readOnly]="!canEdit"
                            [hasCommit]="true"
                            [onCommit]="saveTextType"
                            [fadePreview]="hasFadePreview"
                            [hasUploadFile]="true"
                            [itemTypeId]="itemTypeId"
                            [objectId]="itemId"
                            (valChange)="save(attr, attr.isNew ? 'new' : 'mod', $event)"
                        ></s25-ng-editable-custom-attribute>
                    }
                </div>
                @if (attr.isNew) {
                    <p class="ngFinePrint ngRed">Edit this custom attribute to add it</p>
                }
            }
            @if (canEdit) {
                @if (showDropdown()) {
                    <s25-custom-attr-dropdown
                        [itemTypeId]="itemTypeId"
                        [filter]="filterCustomAttrs"
                        [useSecurity]="true"
                        (chosenChange)="addCustomAttr($event)"
                    ></s25-custom-attr-dropdown>
                } @else {
                    <button
                        class="aw-button aw-button--primary c-margin-top--half add-cust-attr-btn"
                        (click)="showDropdown.set(true)"
                    >
                        Add a Custom Attribute
                    </button>
                }
                <s25-ng-fading-success-msg
                    [message]="successMsg"
                    class="c-margin-top--quarter"
                ></s25-ng-fading-success-msg>
            }
        }
    `,
    styles: `
        @container custom-attr (width <= 600px) {
            .attr-container {
                align-items: flex-start !important;

                .attributeRow {
                    display: flex !important;
                    flex-direction: column;
                    align-items: flex-start !important;

                    & > label {
                        position: relative;
                        top: 3px;
                    }

                    & > div {
                        width: 100%;

                        .ngDropdownPaginated {
                            width: 100% !important;
                        }

                        s25-timepicker,
                        s25-datepicker {
                            display: inline-block;
                            margin-top: 10px;
                        }
                    }
                }
            }

            .add-cust-attr-btn {
                width: 100%;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class S25ObjectDetailsCustomAttrComponent implements OnInit {
    @Input({ required: true }) customAttrs: CustomAttributes.CustAttrObj[];
    @Input({ required: true }) itemId: number;
    @Input({ required: true }) itemTypeId: number;
    @Input({ required: true }) canEdit: boolean;
    @ViewChildren(S25EditableCustomAttributeComponent)
    custAttrComponent: QueryList<S25EditableCustomAttributeComponent>;

    init = signal(false);
    showDropdown = signal(false);
    successMsg = signal("");
    fadePreview = signal(true);
    AttrType = CustomAttributes.type;
    timePref: string;
    dateTimePref: string;
    textToCommit: {
        attribute: CustAttrObj;
        textValue: string;
    };
    dateTimeTypes: CustAttrCode[] = [this.AttrType.Date, this.AttrType.DateTime, this.AttrType.Time];

    constructor(private cd: ChangeDetectorRef) {}

    async ngOnInit() {
        const [timePref, dateTimePref] = await Promise.all([
            UserprefService.getS25Timeformat(),
            UserprefService.getS25DateTimeformat(),
        ]);
        this.timePref = timePref;
        this.dateTimePref = dateTimePref;
        this.init.set(true);
    }

    @Bind
    filterCustomAttrs(item: DropDownItem) {
        return !this.customAttrs.find((attr) => attr.custAttrId === item.val);
    }

    addCustomAttr(attr: DropDownItem) {
        const { itemId, itemName, custAtrbType, multi_val, imageId, sort_order } = attr;
        this.customAttrs.push({
            custAttrId: +itemId,
            imageId: imageId,
            itemId: +itemId,
            itemLabel: itemName,
            itemName: null,
            itemTypeId: custAtrbType,
            multi_val: multi_val,
            sort_order: sort_order,
            isNew: true,
        });
        this.showDropdown.set(false);
    }

    async save(attr: CustAttrObj, status: ServiceStatus, value: any, isTextCommit?: boolean) {
        let newValue: string;
        let isFile = attr.itemTypeId === CustomAttributes.type.URL && value?.fileRef;
        if (isFile) {
            newValue = `object_type=${this.itemTypeId}&object_id=${this.itemId}&atrb_id=${attr.itemId}&file_name=${value.name}`;
            const [ok, err] = await S25Util.Maybe(
                AttachmentService.postFile(this.itemTypeId, this.itemId, value.fileRef, value.name, attr.itemId),
            );
            if (err) return this.onError(err);
            if (ok) {
                this.custAttrComponent.forEach((caComp) => {
                    if (caComp.attributeId === attr.itemId) {
                        caComp.value = newValue;
                        caComp.onRefresh(newValue);
                    }
                });
            }
        }

        if (S25Util.isUndefined(value) && attr.isNew) {
            this.customAttrs = this.customAttrs.filter((item) => attr.custAttrId !== item.custAttrId);
            this.cd.markForCheck();
            return;
        }
        const { Float, Integer, Text, LongText, URL } = this.AttrType;
        if (
            !isTextCommit &&
            ([Float, Integer, Text, LongText, URL] as CustAttrCode[]).includes(attr.itemTypeId) &&
            status !== "del" &&
            !isFile
        ) {
            this.textToCommit = {
                attribute: attr,
                textValue: value,
            };
            return;
        }
        if (this.itemTypeId === Item.Ids.Location) return this.saveSpace(attr, status, value, isFile, newValue);
        const nodeName = this.itemTypeId === Item.Ids.Contact ? "attribute" : "custom_attribute";
        const payload: Partial<Record<typeof nodeName, { status: ServiceStatus; attribute_id: number }>> = {};
        payload[nodeName] = {
            status: status,
            attribute_id: attr.custAttrId,
            ...(status !== "del" && {
                attribute_value: isFile ? newValue : (value.itemId ?? value),
            }),
        };
        let updateAttrsPromise;
        switch (this.itemTypeId) {
            case Item.Ids.Contact:
                updateAttrsPromise = ContactService.putContactMinimal(this.itemId, payload);
                break;
            case Item.Ids.Organization:
                updateAttrsPromise = OrganizationService.putOrganization(this.itemId, payload);
                break;
        }
        const [_, error] = await S25Util.Maybe(updateAttrsPromise);
        if (error) return console.error(error);
        this.onAttrUpdate(attr, status, value);
    }

    async saveSpace(attr: CustAttrObj, status: ServiceStatus, value: any, isFile: boolean, newValue: any) {
        let payload: any = {
            attributes: [
                {
                    attributeId: attr.custAttrId,
                    ...(status !== "del" && { value: isFile ? newValue : (value.itemId ?? value) }),
                },
            ],
        };
        if (status === "del") {
            payload = { remove: payload };
        }
        const [_, error] = await S25Util.Maybe(SpaceService.microUpdate({ id: this.itemId, ...payload }));
        if (error) return console.error(error);
        this.onAttrUpdate(attr, status, value);
    }

    async onAttrUpdate(attr: CustAttrObj, status: ServiceStatus, value: any) {
        this.successMsg.set(`${attr.itemLabel} successfully ${status === "del" ? "removed" : "updated"}`);
        attr.isNew &&= false;
        if (attr.itemTypeId === this.AttrType.LongText) attr.itemName = value; // apply fade preview for long text types if applicable on update
        if (status === "del") {
            this.customAttrs = this.customAttrs.filter((item) => attr.custAttrId !== item.custAttrId);
            //if url type and upload file, when delete CA also delete file
            if (attr.itemTypeId === CustomAttributes.type.URL && value.includes("object_type=")) {
                const params = new URLSearchParams(value);
                const fileName = params.get("file_name");
                await AttachmentService.delFile(this.itemTypeId, this.itemId, fileName);
            }
        }
        this.textToCommit = null;
        this.cd.markForCheck();
    }

    applyFadePreview(hasFadePreview: boolean) {
        if (hasFadePreview) this.fadePreview.set(!this.fadePreview());
    }

    @Bind
    saveTextType() {
        const { attribute, textValue } = this.textToCommit;
        const status = attribute.isNew ? "new" : "mod";
        return this.save(attribute, status, textValue, true);
    }

    async onError(error: any) {
        const msg = error?.error?.results?.error?.msg;
        if (msg) {
            return alert(msg);
        } else {
            return alert("An unexpected error occurred.");
        }
    }

    protected readonly S25Util = S25Util;
}
